/**
 * MKS-464 — Manager overview "Delivery" tab API client.
 *
 * Typed wrapper around `GET /api/manager/delivery`. The types mirror, name for name, the contract
 * exported by `backend/src/services/deliveryOverviewService.ts`; the route's response schema
 * enumerates the same keys, so anything not listed here never reaches the browser.
 *
 * `deliveredAt` / `lastDeliveryAt` are ISO instants; `deliveredDay` and the period's
 * `fromDay` / `toDay` are Europe/Prague calendar days (`YYYY-MM-DD`).
 */
import { apiGet } from './client';

export type DeliveryPeriodKey = 'today' | 'yesterday' | '7d' | '14d' | '30d';

export type DeliveryPersonStatus = 'nothing' | 'hours_no_tasks' | 'delivering' | 'on_leave';

export type CoverageTone = 'ok' | 'amber' | 'red';

export interface DeliveryPersonRef {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  avatarColorSeq: number | null;
  avatarUrl: string | null;
}

export interface DeliveryTaskRef {
  id: number;
  code: string;
  name: string;
  projectId: number;
  projectName: string;
  deliveredAt: string | null;
  deliveredDay: string | null;
  assigneeId: number | null;
  assigneeName: string | null;
}

/**
 * A delivered task the person worked on without being the assignee credited at delivery. Every
 * field is the delivery's own; `contributedHours` is the person's hours on it up to and including
 * the delivery day.
 */
export interface DeliveryContributedTaskRef extends DeliveryTaskRef {
  contributedHours: number;
}

export type DeliverySpecRole = 'created' | 'acceptance' | 'answered' | 'description';

/**
 * A delivered task the person co-authored without being the assignee credited at delivery. Every
 * field is the delivery's own; `specRoles` says how the person co-authored it (fixed order).
 */
export interface DeliverySpecifiedTaskRef extends DeliveryTaskRef {
  specRoles: DeliverySpecRole[];
}

export interface DeliveryPersonRow {
  person: DeliveryPersonRef;
  status: DeliveryPersonStatus;
  onLeave: boolean;
  delivered: number;
  specified: number;
  contributed: number;
  inProgress: number;
  hours: number;
  taskHours: number;
  coveragePct: number | null;
  coverageTone: CoverageTone | null;
  lastDeliveryAt: string | null;
  deliveredTasks: DeliveryTaskRef[];
  specifiedTasks: DeliverySpecifiedTaskRef[];
  contributedTasks: DeliveryContributedTaskRef[];
  inProgressTasks: DeliveryTaskRef[];
}

/** A Factory/Robot delivery plus the humans who co-authored it ("Lastname Firstname", sorted). */
export interface DeliveryBotTaskRef extends DeliveryTaskRef {
  specifiedBy: string[];
}

export interface DeliveryBotsGroup {
  delivered: number;
  inProgress: number;
  deliveredTasks: DeliveryBotTaskRef[];
  inProgressTasks: DeliveryTaskRef[];
}

export interface DeliveryProjectRow {
  id: number;
  name: string;
  delivered: number;
  inProgress: number;
  hours: number;
  people: DeliveryPersonRef[];
  lastDeliveryAt: string | null;
  deliveredTasks: DeliveryTaskRef[];
  inProgressTasks: DeliveryTaskRef[];
}

export interface DeliveryPeriodInfo {
  key: DeliveryPeriodKey;
  label: string;
  from: string;
  to: string;
  fromDay: string;
  toDay: string;
}

export interface DeliveryOverview {
  period: DeliveryPeriodInfo;
  people: DeliveryPersonRow[];
  bots: DeliveryBotsGroup;
  projects: DeliveryProjectRow[];
}

export async function fetchDeliveryOverview(period: DeliveryPeriodKey): Promise<DeliveryOverview> {
  const params = new URLSearchParams();
  params.set('period', period);
  const res = await apiGet<{ data: DeliveryOverview }>(`/manager/delivery?${params.toString()}`);
  return res.data;
}

// MKS-520 — GET /api/manager/delivery/weekly-trend.
// Mirrors `backend/src/services/deliveryWeeklyTrendService.ts`:
// the last 8 Europe/Prague calendar weeks, oldest first, the current partial week last; `totals[i]`
// is the person's Total (delivered + specified + contributed) in week i.

export interface DeliveryTrendWeek {
  /** Monday, `YYYY-MM-DD`. */
  weekStart: string;
  /** Sunday, or today for the current week, `YYYY-MM-DD`. */
  weekEnd: string;
  /** ISO week, `YYYY-Www`. */
  label: string;
}

export interface DeliveryTrendPerson {
  personId: number;
  totals: number[];
}

export interface DeliveryWeeklyTrend {
  weeks: DeliveryTrendWeek[];
  people: DeliveryTrendPerson[];
}

export async function fetchDeliveryWeeklyTrend(): Promise<DeliveryWeeklyTrend> {
  const res = await apiGet<{ data: DeliveryWeeklyTrend }>('/manager/delivery/weekly-trend');
  return res.data;
}
